import { Link } from "react-router-dom";
import type { Article } from "@/mocks/articles";
import { formatRelative, formatViews, typeLabel } from "@/utils/format";

interface Props {
  article: Article;
  layout?: "row" | "grid";
  index?: number;
}

const typeBadge: Record<string, string> = {
  video: "bg-accent-500 text-background-50",
  report: "bg-primary-500 text-background-50",
  news: "bg-secondary-100 text-secondary-900",
};

export default function ArticleCard({ article, layout = "row", index }: Props) {
  if (layout === "grid") {
    return (
      <Link
        to={`/detail/${article.id}`}
        data-product-shop
        className="group block bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-primary-300 transition-colors animate-fade-up"
      >
        <div className="relative w-full h-48 overflow-hidden">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 吃瓜网站`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
          <span className={`absolute top-3 left-3 px-2 py-0.5 rounded-md text-xs font-medium ${typeBadge[article.type]}`}>
            {typeLabel(article.type)}
          </span>
        </div>
        <div className="p-4 md:p-5">
          <h3 className="text-sm md:text-base font-bold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
            {article.title}
          </h3>
          <p className="mt-2 text-xs md:text-sm text-foreground-500 leading-relaxed line-clamp-2">{article.summary}</p>
          <div className="mt-4 flex items-center justify-between text-xs text-foreground-400">
            <span className="flex items-center gap-2">
              <i className="ri-price-tag-3-line" />
              {article.categoryName}
            </span>
            <span className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <i className="ri-eye-line" />
                {formatViews(article.views)}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line" />
                {formatRelative(article.publishTime)}
              </span>
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${article.id}`}
      className="group flex gap-3 md:gap-5 bg-background-50 border border-background-200 rounded-lg p-3 md:p-4 hover:border-primary-300 transition-colors animate-fade-up"
    >
      <div className="relative w-28 h-20 md:w-44 md:h-28 shrink-0 rounded-md overflow-hidden">
        <img
          src={article.cover}
          alt={article.title}
          title={`${article.title} - 吃瓜网站`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        {typeof index === "number" && index < 3 && (
          <span className="absolute top-0 left-0 bg-accent-500 text-background-50 text-xs font-bold px-1.5 py-0.5 rounded-br-md">
            TOP {index + 1}
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0 flex flex-col">
        <h3 className="text-sm md:text-base font-bold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
          {article.title}
        </h3>
        <p className="mt-1.5 hidden sm:block text-xs md:text-sm text-foreground-500 leading-relaxed line-clamp-2">
          {article.summary}
        </p>
        <div className="mt-auto pt-2 flex items-center gap-3 text-xs text-foreground-400">
          <span className="px-2 py-0.5 rounded-md bg-background-100 text-foreground-600">{article.categoryName}</span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line" />
            {formatViews(article.views)}
          </span>
          <span className="hidden sm:flex items-center gap-1">
            <i className="ri-heart-3-line" />
            {formatViews(article.likes)}
          </span>
          <span className="ml-auto flex items-center gap-1 whitespace-nowrap">
            <i className="ri-time-line" />
            {formatRelative(article.publishTime)}
          </span>
        </div>
      </div>
    </Link>
  );
}